import { createFileRoute, Link } from "@tanstack/react-router";
import { ArrowUpRight, Search, Code2, Bot, Gauge, Wrench } from "lucide-react";
import { Section } from "@/components/Section";
import { Reveal } from "@/components/Reveal";
import { JsonLd } from "@/components/JsonLd";
import { Breadcrumbs } from "@/components/Breadcrumbs";
import { FaqAccordion } from "@/components/FaqAccordion";
import { buildSeo, breadcrumbSchema } from "@/lib/seo";
import { faqSchema } from "@/lib/schema";
import { SITE } from "@/content/site";

export const Route = createFileRoute("/tools/")({
  head: () =>
    buildSeo({
      title: "Free SEO Tools — SERP Simulator, Schema Generator & Robots.txt Tester",
      description:
        "Free, no-signup SEO tools built by a working SEO consultant: pixel-accurate SERP preview, JSON-LD schema generator (10 types), robots.txt tester for Googlebot, GPTBot, PerplexityBot and more.",
      path: "/tools",
      ogEyebrow: "Free Tools",
      keywords:
        "free SEO tools, SERP simulator, schema generator, robots.txt tester, JSON-LD generator, GPTBot tester",
    }),
  component: ToolsHub,
});

type Tool = {
  to: string;
  external?: boolean;
  title: string;
  blurb: string;
  icon: React.ComponentType<{ size?: number; className?: string }>;
  tags: string[];
};

const TOOLS: Tool[] = [
  {
    to: "/tools/serp-simulator",
    title: "SERP Simulator",
    blurb: "Pixel-accurate Google SERP preview for desktop and mobile. Catches title and meta truncation before they go live.",
    icon: Search,
    tags: ["On-page", "Snippets"],
  },
  {
    to: "/tools/schema-generator",
    title: "Schema Markup Generator",
    blurb: "Generate valid JSON-LD for 10 schema types — Article, FAQ, HowTo, LocalBusiness, Product, Person, Organization, Service, Review, Breadcrumb.",
    icon: Code2,
    tags: ["Schema", "AEO"],
  },
  {
    to: "/tools/robots-tester",
    title: "Robots.txt Tester",
    blurb: "Test if Googlebot, GPTBot, ClaudeBot, PerplexityBot or any user-agent is allowed to crawl a URL — with the exact rule that decided it.",
    icon: Bot,
    tags: ["Crawl", "AI bots"],
  },
  {
    to: "/free-seo-audit",
    title: "Free SEO Audit (snapshot)",
    blurb: "Drop a URL for a quick heuristic snapshot across HTTPS, titles, meta, H1, sitemap and schema.",
    icon: Gauge,
    tags: ["Audit"],
  },
  {
    to: "https://tool.freelancertamal.com/",
    external: true,
    title: "Full SEO Audit Tool",
    blurb: "Deeper crawl-based audit on tool.freelancertamal.com — Core Web Vitals, on-page, technical signals. Free, no signup.",
    icon: Wrench,
    tags: ["Audit", "Crawl"],
  },
];

const FAQS = [
  {
    q: "Are these tools really free?",
    a: "Yes. They run entirely in your browser — no signup, no rate limits, no data sent to a server. They're free forever as a way to share work with the SEO community.",
  },
  {
    q: "Do you store any data I paste in?",
    a: "No. Everything is processed client-side in your browser. We don't have a database or analytics on your input — refresh the page and it's gone.",
  },
  {
    q: "Can I embed these tools on my own site?",
    a: "Not yet. Embeddable widget versions are on the roadmap. In the meantime feel free to link to them from your blog posts or internal docs.",
  },
  {
    q: "Why are you giving these away?",
    a: "Because most SEO problems are small enough to solve with a tool. The work I charge for is the strategy, the audit synthesis, and the implementation. The utilities should be free.",
  },
];

function ToolsHub() {
  return (
    <>
      <Breadcrumbs items={[{ name: "Home", path: "/" }, { name: "Tools", path: "/tools" }]} />
      <Section headingLevel={1}
        eyebrow="Free Tools"
        title={<>Free SEO tools <span className="text-gradient">built by a working SEO.</span></>}
        subtitle="No signup. No rate limits. Everything runs in your browser. Use them daily, embed the JSON-LD they spit out, link to them from your blog."
      >
        <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
          {TOOLS.map((t, i) => {
            const Icon = t.icon;
            return (
              <Reveal key={t.to} delay={(i % 3) * 0.06}>
                {t.external ? (
                  <a
                    href={t.to}
                    target="_blank"
                    rel="noopener"
                    className="group block h-full rounded-3xl border border-border bg-card p-7 hover:border-primary/40 transition"
                  >
                    <ToolCardInner Icon={Icon} t={t} />
                  </a>
                ) : (
                  <Link
                    // eslint-disable-next-line @typescript-eslint/no-explicit-any
                    to={t.to as any}
                    className="group block h-full rounded-3xl border border-border bg-card p-7 hover:border-primary/40 transition"
                  >
                    <ToolCardInner Icon={Icon} t={t} />
                  </Link>
                )}
              </Reveal>
            );
          })}
        </div>
      </Section>

      <Section eyebrow="FAQ" title="Common questions about the tools.">
        <div className="max-w-3xl">
          <FaqAccordion items={FAQS} withSchema={false} />
        </div>
      </Section>

      <JsonLd
        data={{
          "@context": "https://schema.org",
          "@type": "CollectionPage",
          name: "Free SEO Tools",
          url: `${SITE.url}/tools`,
          description: "Free browser-based SEO tools — SERP simulator, schema generator, robots.txt tester.",
          mainEntity: {
            "@type": "ItemList",
            itemListElement: TOOLS.map((t, i) => ({
              "@type": "ListItem",
              position: i + 1,
              url: t.external ? t.to : `${SITE.url}${t.to}`,
              name: t.title,
            })),
          },
        }}
      />
      <JsonLd
        data={[
          faqSchema(FAQS),
          breadcrumbSchema([{ name: "Home", path: "/" }, { name: "Tools", path: "/tools" }]),
        ]}
      />
    </>
  );
}

function ToolCardInner({ Icon, t }: { Icon: Tool["icon"]; t: Tool }) {
  return (
    <>
      <div className="flex items-center justify-between">
        <span className="inline-flex h-12 w-12 items-center justify-center rounded-2xl bg-primary/10 text-primary">
          <Icon size={22} />
        </span>
        <ArrowUpRight size={18} className="opacity-40 group-hover:opacity-100 transition" />
      </div>
      <div className="mt-5 font-display text-2xl leading-snug">{t.title}</div>
      <p className="mt-2 text-sm text-muted-foreground">{t.blurb}</p>
      <div className="mt-4 flex flex-wrap gap-2">
        {t.tags.map((tag) => (
          <span key={tag} className="text-[10px] uppercase tracking-[0.18em] text-primary/80 border border-primary/30 rounded-full px-2 py-1">
            {tag}
          </span>
        ))}
      </div>
    </>
  );
}